import React from 'react';
import { motion } from 'framer-motion';
import { 
  BookOpen, 
  Users, 
  Target, 
  Calendar,
  Clock,
  CheckCircle,
  Award
} from 'lucide-react';

const curriculum = [
  {
    session: '1a',
    title: 'Introduction to Food Export Business',
    topics: [
      'Overview of global food trade',
      'Market opportunities and trends',
      'Key success factors in food export',
      'Understanding food safety standards'
    ]
  },
  {
    session: '1b',
    title: 'Product Selection and Preparation',
    topics: [
      'Identifying profitable food products',
      'Quality control measures',
      'Packaging and preservation',
      'Storage requirements'
    ]
  },
  {
    session: '2a',
    title: 'Documentation and Compliance',
    topics: [
      'Export documentation requirements',
      'Food safety certifications',
      'International standards compliance',
      'Legal requirements'
    ]
  },
  {
    session: '2b',
    title: 'Market Entry and Buyer Connection',
    topics: [
      'Market research techniques',
      'Buyer identification strategies',
      'Negotiation skills',
      'Price calculation'
    ]
  },
  {
    session: '3a',
    title: 'Logistics and Supply Chain',
    topics: [
      'Transportation methods',
      'Cold chain management',
      'Freight forwarding',
      'Insurance requirements'
    ]
  },
  {
    session: '3b',
    title: 'Business Growth and Scaling',
    topics: [
      'Financial management',
      'Risk mitigation',
      'Business expansion strategies',
      'Building lasting relationships'
    ]
  }
];

{/* const instructors = [
  {
    name: 'Ibironke Delly',
    role: 'AgX Food Export',
    expertise: '8+ years in food export business',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80'
  },
  {
    name: 'Adura XYZ',
    role: 'DuraFoodExport',
    expertise: 'Specialist in UK, US, foodstuff market',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80'
  },
  {
    name: 'Titilope ',
    role: 'Fayfoods NG',
    expertise: '5+ years in food packaging & export',
    image: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80'
  }
]; */}

const FEMDetails = () => {
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Course Overview */}
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-gray-900 sm:text-4xl mb-4"
          >
            Program Details
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            A comprehensive program designed to equip you with the knowledge and skills needed to succeed in the international food export business.
          </motion.p>
        </div>

        {/* Key Information Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-xl p-6 shadow-lg"
          >
            <Clock className="h-8 w-8 text-green-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Duration</h3>
            <p className="text-gray-600">2 days intensive training</p>
            <ul className="mt-4 space-y-2">
              <li className="flex items-center text-gray-600">
                <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                3 practical sessions
              </li>
              <li className="flex items-center text-gray-600">
                <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                2 hours Q & A
              </li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-6 shadow-lg"
          >
            <Target className="h-8 w-8 text-green-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Learning Format</h3>
            <p className="text-gray-600">Virtual learning approach</p>
            <ul className="mt-4 space-y-2">
              <li className="flex items-center text-gray-600">
                <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                Virtual live sessions
              </li>
              <li className="flex items-center text-gray-600">
                <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                Practical mentorship
              </li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl p-6 shadow-lg"
          >
            <Award className="h-8 w-8 text-green-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Certification</h3>
            <p className="text-gray-600">Industry-recognized certification</p>
            <ul className="mt-4 space-y-2">
              <li className="flex items-center text-gray-600">
                <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                NEPC approved
              </li>
              <li className="flex items-center text-gray-600">
                <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                Internationally accepted
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Curriculum */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">Course Curriculum</h3>
          <div className="grid gap-6">
            {curriculum.map((session, index) => (
              <motion.div
                key={session.session}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="border-l-4 border-green-600 pl-6 py-4"
              >
                <h4 className="text-xl font-semibold text-gray-900 mb-2">
                  Session {session.session}: {session.title}
                </h4>
                <ul className="grid md:grid-cols-2 gap-3">
                  {session.topics.map((topic, topicIndex) => (
                    <li key={topicIndex} className="flex items-center text-gray-600">
                      <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
                      {topic}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Instructors 
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">Meet Your Instructors</h3>
          <div className="grid md:grid-cols-3 gap-8">
            {instructors.map((instructor, index) => (
              <motion.div
                key={instructor.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="text-center"
              >
                <img
                  src={instructor.image}
                  alt={instructor.name}
                  className="w-32 h-32 rounded-full mx-auto mb-4 object-cover"
                />
                <h4 className="text-xl font-semibold text-gray-900">{instructor.name}</h4>
                <p className="text-green-600 font-medium">{instructor.role}</p>
                <p className="text-gray-600 mt-2">{instructor.expertise}</p>
              </motion.div>
            ))}
          </div>
        </div> */}
      </div>
    </section>
  );
};

export default FEMDetails;